/**
 * Thin typed fetch wrapper.
 *
 * openapi-typescript emits types only (no runtime client), so this is the
 * single hand-written transport — generic `get`/`post` over global `fetch`
 * with a per-request timeout. The endpoint-specific request/response *shapes*
 * are pulled from the generated `schema.ts` at the call sites in client.ts, so
 * the wire contract still lives in spec/openapi.yaml. Methods return the raw
 * `Response` so the caller can read the exact bytes the server signed before
 * any decode (judge signature verification) — mirroring the Python surface's
 * use of raw httpx (DECISIONS 2026-05-22).
 */
export type QueryValue = string | number | boolean | undefined | null;

export class HttpClient {
  readonly baseUrl: string;
  readonly timeoutMs: number;

  constructor(baseUrl: string, timeoutMs: number) {
    this.baseUrl = baseUrl.replace(/\/+$/, "");
    this.timeoutMs = timeoutMs;
  }

  buildUrl(path: string, query?: Record<string, QueryValue>): string {
    const url = new URL(this.baseUrl + path);
    if (query) {
      for (const [k, v] of Object.entries(query)) {
        if (v !== undefined && v !== null && v !== "") {
          url.searchParams.set(k, String(v));
        }
      }
    }
    return url.toString();
  }

  async get(
    path: string,
    query?: Record<string, QueryValue>,
    headers?: Record<string, string>,
  ): Promise<Response> {
    return this.fetch(this.buildUrl(path, query), {
      method: "GET",
      ...(headers && { headers }),
    });
  }

  async post(
    path: string,
    body: unknown,
    headers?: Record<string, string>,
  ): Promise<Response> {
    return this.fetch(this.buildUrl(path), {
      method: "POST",
      headers: { "Content-Type": "application/json", ...headers },
      body: JSON.stringify(body),
    });
  }

  private async fetch(url: string, init: RequestInit): Promise<Response> {
    return fetch(url, { ...init, signal: AbortSignal.timeout(this.timeoutMs) });
  }
}
